/* ===================================================================
   JOMIKUTO · Bag aria-label live sync + ATC click-guard
   ---------------------------------------------------------------
   v40 notes:
   - ATC instant-label-change lives in product-form.js onSubmit (integrated
     cleanly with theme, no capture-phase race, no minifier risk).
   - THIS FILE adds a minimal click-level guard that applies .is-loading
     BEFORE the form submit event fires. Only job: add a class so
     pointer-events: none CSS kicks in and blocks double-taps during the
     click-to-submit gap (~100-500ms browser form-submit prep time).
   - Also: bag icon aria-label live sync.

   This is the "middle option" from Cowork v39 feedback: double-tap
   protection without re-introducing the capture-phase flag that
   Shopify's minifier keeps stripping.
   =================================================================== */

(() => {
  'use strict';

  const ATC_SELECTOR = 'form[action$="/cart/add"] button[name="add"], form[action*="/cart/add"] button[name="add"], .jomi-pdp__atc, [data-jomi-atc]';
  const BAG_SELECTOR = '[data-jomi-cart-trigger], .jomi-header__cart-btn';

  // ---- 1. Click-level guard (double-tap protection during form-submit prep) ----
  // Adds .is-loading class only. Does NOT set disabled (that broke v37).
  // Does NOT change label (product-form.js handles that synchronously in onSubmit).
  // Does NOT call preventDefault (form submission must proceed normally).
  // Bubble-phase is fine — all we need is the class applied before the next click.
  document.addEventListener('click', (e) => {
    const btn = e.target.closest(ATC_SELECTOR);
    if (!btn) return;
    if (btn.classList.contains('is-loading')) return; // already in progress
    btn.classList.add('is-loading');
  });

  // ---- 2. Bag aria-label live sync ----
  function updateBagAriaLabel(cart) {
    if (!cart || typeof cart.item_count !== 'number') return;
    const n = cart.item_count;
    const label = 'Bag, ' + n + ' item' + (n === 1 ? '' : 's');
    document.querySelectorAll(BAG_SELECTOR).forEach((el) => {
      el.setAttribute('aria-label', label);
    });
  }

  // Refresh aria-label on every cart:updated emit from the theme's event bus
  document.addEventListener('cart:updated', (e) => {
    const cart = e?.detail?.cart || e?.detail;
    if (cart?.item_count !== undefined) updateBagAriaLabel(cart);
    else fetch('/cart.js').then((r) => r.json()).then(updateBagAriaLabel).catch(() => {});
  });

  // Backstop: patch window.fetch to catch cart mutations that bypass the event bus
  const origFetch = window.fetch;
  window.fetch = async (...args) => {
    const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
    const res = await origFetch.apply(window, args);
    try {
      if (/\/cart\/(add|change|update|clear)(\.js)?/.test(url)) {
        // Defer a tick so any cart:updated handlers get to run first
        setTimeout(() => {
          fetch('/cart.js').then((r) => r.json()).then(updateBagAriaLabel).catch(() => {});
        }, 50);
      }
    } catch {}
    return res;
  };

  // Initial sync on load, in case the page was cached with a stale aria-label
  document.addEventListener('DOMContentLoaded', () => {
    fetch('/cart.js').then((r) => r.json()).then(updateBagAriaLabel).catch(() => {});
  });
})();
